import { MA_SYNERGY_TRACKING_METRIC_REGISTRY_SEED_INPUTS } from './metric-registry.ma-synergy-tracking.data';
import { ORG_ANALYSIS_OPTIMIZATION_METRIC_REGISTRY_SEED } from './metric-registry.org-analysis-optimization';
import { ORG_GRAPH_READ_METRIC_REGISTRY_SEED } from './metric-registry.org-graph-read';
import {
  OPERATING_EXPENSE_PER_HEAD_MEASURE_IDENTITY,
  OPERATING_INCOME_MEASURE_IDENTITY,
  type GovernedMeasureIdentity,
} from './governed-measure-identity';
import {
  SGA_COST_POOL_METRIC_REGISTRY_SEED_INPUTS,
} from './metric-registry.sga-cost-pool';
import {
  GL_DATA_LAKE_METRIC_REGISTRY_SEED_INPUTS,
  GOVERNED_DIMENSION_CATALOG_SEED,
} from './metric-registry.gl-data-lake.data';
import { SGA_SPEND_EXISTING_REGISTRY_SEED_INPUTS } from './metric-registry.sga-spend-existing.data';
import { MA_DEAL_METRIC_REGISTRY_SEED_INPUTS } from './metric-registry.ma-deal.data';
import { FINANCE_3PL_SUPPLEMENTAL_METRIC_REGISTRY_SEED_INPUTS } from './metric-registry.finance-3pl-supplemental.data';
import { SGA_SPEND_SOURCE_METRIC_REGISTRY_SEED_INPUTS } from './metric-registry.sga-spend-source.data';
import { INDUSTRY_BENCHMARK_LENS_METRIC_REGISTRY_SEED_INPUTS } from './metric-registry.industry-benchmark-lens.data';
import { FINANCE_LEVER_CONTRIBUTION_METRIC_REGISTRY_SEED_INPUTS_A } from './metric-registry.finance-lever-contributions-a.data';
import { WORKFORCE_REDUCTION_METRIC_REGISTRY_SEED_INPUTS } from './metric-registry.workforce-reduction.data';

export const METRIC_REGISTRY_SCHEMA_VERSION = 'metric_registry_v1' as const;
export const GOVERNED_METRIC_FACT_ALL_MEMBER_KEY = '__all__' as const;
export const METRIC_REGISTRY_GLOBAL_TENANT_ID = '__global__' as const;
// The 3PL metric seed uses '3pl-logistics'; module and GL scopes use 'logistics-3pl'.
// NEITHER side can be renamed from code: the GL catalog identity is digest-pinned
// to a live ACTIVE row, and the seed identities are what candidate rows were
// projected from. The reconciliation is therefore a TRANSLATION applied at
// request-scope matching time only — persisted identities, projections, and
// digests are untouched. '3pl-logistics' is canonical (the 26-definition compiled
// estate); 'logistics-3pl' is the declared legacy spelling, confined to the one
// live-certified GL row and the module-factory configs until the governed re-mint
// (industry-profile-id-consistency.spec.ts spells out the live-DB remediation).
export const CANONICAL_INDUSTRY_PROFILE_ID_BY_LEGACY_ALIAS: Readonly<
  Record<string, string>
> = Object.freeze({ 'logistics-3pl': '3pl-logistics' });
/** Normalize an industry profile id to its canonical spelling for SCOPE MATCHING. */
export function normalizeIndustryProfileId(id: string | undefined): string | undefined {
  if (id === undefined) return undefined;
  return CANONICAL_INDUSTRY_PROFILE_ID_BY_LEGACY_ALIAS[id] ?? id;
}
export * from './metric-registry.contracts';
import type {
  DimensionCatalogEntry,
  DimensionSeed,
  GovernedMetricFactDedupInput,
  GovernedMetricFactDedupResult,
  MetricBlendRequest,
  MetricComponentRef,
  MetricDirection,
  MetricQuerySpec,
  MetricRegistryAggregationRule,
  MetricRegistryAppliesTo,
  MetricRegistryBlendPolicy,
  MetricRegistryCohortQualifier,
  MetricRegistryComponents,
  MetricRegistryComposition,
  MetricRegistryCompositionGap,
  MetricRegistryEntry,
  MetricRegistryLayer,
  MetricRegistryNamedGap,
  MetricRegistryRefusalReason,
  MetricRegistryRefused,
  MetricRegistrySignConvention,
  MetricRegistrySourceOfTruthClass,
  MetricRegistryTargetRef,
  MetricRegistryValidationResult,
  MetricRollupGrain,
  MetricTargetComparisonAnchor,
  MetricTemporalType,
} from './metric-registry.contracts';

export {
  CHRW_BUSINESS_LINE_DIMENSION_CATALOG,
  GOVERNED_DIMENSION_CATALOG_SEED,
  HELIX_RETAIL_GL_ACCOUNT_DIMENSION_CATALOG,
} from './metric-registry.gl-data-lake.data';

const USD_AGGREGATION_RULES: readonly MetricRegistryAggregationRule[] = [
  {
    dimension: 'business_line',
    rollup: 'reported_segment',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'governed_derived_fact',
  },
  {
    dimension: 'business_line',
    rollup: 'enterprise',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'governed_derived_fact',
  },
  {
    dimension: 'period',
    rollup: 'fiscal_year',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'computed_on_demand',
  },
  {
    dimension: 'period',
    rollup: 'quarter',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'computed_on_demand',
  },
];

const RATIO_AGGREGATION_RULES: readonly MetricRegistryAggregationRule[] = [
  {
    dimension: 'business_line',
    rollup: 'reported_segment',
    ruleFormulaId: 'ratio_recompute_v1',
    materialization: 'computed_on_demand',
  },
  {
    dimension: 'business_line',
    rollup: 'enterprise',
    ruleFormulaId: 'ratio_recompute_v1',
    materialization: 'computed_on_demand',
  },
  {
    dimension: 'period',
    rollup: 'fiscal_year',
    ruleFormulaId: 'ratio_recompute_v1',
    materialization: 'computed_on_demand',
  },
  {
    dimension: 'period',
    rollup: 'quarter',
    ruleFormulaId: 'ratio_recompute_v1',
    materialization: 'computed_on_demand',
  },
];

export const CHRW_BUSINESS_LINE_DIMENSION_SEED: DimensionSeed = {
  dimension: 'business_line',
  label: 'CHRW reportable business line',
  source: 'finance.profile.ts supplementalLineKeys',
  members: [
    {
      dimension: 'business_line',
      memberKey: 'nast',
      label: 'NAST',
      rollup: 'reported_segment',
      sourceLineKeys: [
        'nast_total_revenues_usd',
        'nast_adjusted_gross_profit_usd',
        'nast_income_from_operations_usd',
      ],
    },
    {
      dimension: 'business_line',
      memberKey: 'global_forwarding',
      label: 'Global Forwarding',
      rollup: 'reported_segment',
      sourceLineKeys: [
        'global_forwarding_total_revenues_usd',
        'global_forwarding_adjusted_gross_profit_usd',
        'global_forwarding_income_from_operations_usd',
      ],
    },
    {
      dimension: 'business_line',
      memberKey: 'all_other_corporate',
      label: 'All Other and Corporate',
      rollup: 'reported_segment',
      sourceLineKeys: [
        'all_other_corporate_total_revenues_usd',
        'all_other_corporate_adjusted_gross_profit_usd',
        'all_other_corporate_income_from_operations_usd',
      ],
    },
  ],
};

export const FINANCE_SEMANTIC_LAYER_REGISTRY_SEED: readonly MetricRegistryEntry[] = [
  metricEntry({
    metricKey: 'revenue_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Revenue',
    definition: 'Revenue from the approved accounting artifact.',
    reconciliationRule: 'Direct line from approved accounting artifact.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/revenue_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['revenue_usd'],
  }),
  metricEntry({
    metricKey: 'cost_of_revenue_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    direction: 'lower_is_better',
    label: 'Cost of revenue',
    definition: 'Industry-profile cost of revenue from mapped accounting lines.',
    reconciliationRule: 'Direct profile-mapped line from approved accounting artifact.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/cost_of_revenue_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['cost_of_revenue_usd'],
  }),
  metricEntry({
    metricKey: 'gross_profit_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Gross profit',
    definition: 'Revenue less industry-profile cost of revenue.',
    reconciliationRule:
      'Reconciles to revenue minus cost of revenue for the selected industry profile.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/gross_profit_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['gross_profit_usd'],
  }),
  metricEntry({
    metricKey: 'operating_expense_usd',
    measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    direction: 'lower_is_better',
    label: 'Operating expense',
    definition: 'Operating expense below gross profit from approved accounting lines.',
    reconciliationRule: 'Direct profile-mapped operating expense line from approved accounting artifact.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/operating_expense_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['operating_expense_usd'],
  }),
  metricEntry({
    metricKey: 'operating_income_usd',
    label: 'Operating income',
    definition: 'Gross profit less operating expense before EBITDA add-backs.',
    reconciliationRule: 'Reconciles to gross profit minus operating expense.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/operating_income_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['operating_income_usd'],
    measureIdentity: OPERATING_INCOME_MEASURE_IDENTITY,
    // objective-value-bridge bare-string target id + 3PL defaultObjectiveMetric.
    aliases: ['operating_income', 'income_from_operations_usd'],
  }),
  metricEntry({
    metricKey: 'adjusted_operating_income_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Adjusted operating income',
    definition:
      'Adjusted operating income from approved finance evidence where supplied by the company reporting profile.',
    reconciliationRule:
      'Direct profile-mapped line from approved finance evidence, or maps to operating income when the profile has no separate adjusted measure.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/adjusted_operating_income_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['adjusted_operating_income_usd'],
  }),
  metricEntry({
    metricKey: 'depreciation_amortization_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    direction: 'context',
    label: 'D&A',
    definition: 'Depreciation and amortization add-back under the approved EBITDA definition.',
    reconciliationRule: 'Direct D&A line from approved accounting artifact.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/depreciation_amortization_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['depreciation_amortization_usd'],
  }),
  metricEntry({
    metricKey: 'approved_adjustments_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Approved adjustments',
    definition: 'Approved EBITDA adjustments from governed finance evidence.',
    reconciliationRule:
      'Line item and state summary must reconcile when both are supplied; the canonical OI-to-EBITDA bridge does not silently add them.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/approved_adjustments_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['approved_adjustments_usd'],
  }),
  metricEntry({
    metricKey: 'ebitda_usd',
    measureIdentity: { measuredDimension: 'ebitda', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    // Graph Phase 4: the objective taxonomy names this objective
    // `metric:ebitda:v1` (objective-taxonomy-map.json), whose bare name is `ebitda`.
    // Without this alias the flagship EBITDA objective resolved to NOTHING in the
    // governed registry — the taxonomy governed its levers while the metric identity
    // was unlinked. `ebitda` collides with no other canonical key or alias (asserted
    // by metric-id-namespace.conformance.spec.ts).
    aliases: ['ebitda'],
    label: 'EBITDA',
    definition: 'Operating income plus D&A.',
    reconciliationRule: 'Derived EBITDA reconciles to approved operating income and D&A lines.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/ebitda_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['ebitda_usd'],
    sourceOfTruthClass: 'derived',
    // The two governed EBITDA numbers must never be summed: derived (OI + D&A) vs the
    // filed typed_ebitda_usd (memory reference_two_governed_ebitda_numbers). Symmetric edge.
    neverSumWith: ['typed_ebitda_usd'],
    composedFrom: {
      formulaId: 'ebitda_from_operating_income_v1',
      formulaVersion: '1.0.0',
      inputMetricKeys: ['operating_income_usd', 'depreciation_amortization_usd'],
    },
  }),
  metricEntry({
    metricKey: 'typed_ebitda_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Typed EBITDA',
    definition:
      'EBITDA as filed in the approved finance artifact (the standalone reported figure), governed separately from the derived OI + D&A bridge.',
    reconciliationRule:
      'Direct typed EBITDA line from the approved finance artifact; never summed with the derived ebitda_usd.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#canonicalLine:FINANCE_CANONICAL_CORE/typed_ebitda_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['typed_ebitda_usd'],
    neverSumWith: ['ebitda_usd'],
  }),
  metricEntry({
    metricKey: 'gross_margin_pct',
    measureIdentity: { measuredDimension: 'gross_profit', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'revenue' },
    label: 'Gross margin',
    definition: 'Industry-profile gross margin definition.',
    reconciliationRule: 'Uses the selected industry profile numerator and denominator.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/gross_margin_pct',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['gross_margin_pct'],
    unit: 'percent',
    frame: 'margin_pct',
    temporalType: 'ratio',
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: RATIO_AGGREGATION_RULES,
    components: components(['gross_profit_usd'], ['revenue_usd']),
  }),
  metricEntry({
    metricKey: 'net_revenue_margin_pct',
    measureIdentity: { measuredDimension: 'gross_profit', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'revenue' },
    label: 'Net revenue margin',
    definition: 'Adjusted gross profit divided by revenue for profiles that report net revenue.',
    reconciliationRule: 'Uses approved adjusted gross profit and revenue for the same reporting cut.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/net_revenue_margin_pct',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['net_revenue_margin_pct'],
    unit: 'percent',
    frame: 'net_revenue_margin_pct',
    temporalType: 'ratio',
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: RATIO_AGGREGATION_RULES,
    components: components(['gross_profit_usd'], ['revenue_usd']),
  }),
  metricEntry({
    metricKey: 'operating_margin_pct',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'gross_profit' },
    label: 'Operating margin',
    definition: 'Adjusted operating income divided by adjusted gross profit.',
    reconciliationRule: 'Uses the selected industry profile adjusted operating income and adjusted gross profit denominator.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/operating_margin_pct',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['operating_margin_pct'],
    unit: 'percent',
    frame: 'operating_margin_pct',
    temporalType: 'ratio',
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: RATIO_AGGREGATION_RULES,
    components: components(['adjusted_operating_income_usd'], ['gross_profit_usd']),
  }),
  metricEntry({
    metricKey: 'ebitda_margin_pct',
    measureIdentity: { measuredDimension: 'ebitda', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'revenue' },
    label: 'EBITDA margin',
    definition: 'EBITDA divided by revenue.',
    reconciliationRule: 'Derived from approved revenue and EBITDA for the same reporting cut.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/ebitda_margin_pct',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['ebitda_margin_pct'],
    unit: 'percent',
    frame: 'ebitda_margin_pct',
    temporalType: 'ratio',
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: RATIO_AGGREGATION_RULES,
    components: components(['ebitda_usd'], ['revenue_usd']),
    // Benchmark-seam divergence collapse (charter §6.2 / oracle §6.4): the lens carries
    // ebitda_margin and ebitda_margin_percent; register both as exact aliases of the id.
    aliases: ['ebitda_margin', 'ebitda_margin_percent'],
  }),
  metricEntry({
    metricKey: 'reporting_headcount',
    measureIdentity: { measuredDimension: 'other', valueForm: 'count', unitFamily: 'count', normalizationBasis: 'none' },
    label: 'Reporting headcount',
    definition: 'Headcount included in the approved finance artifact.',
    reconciliationRule: 'Direct profile-mapped line from approved accounting artifact.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/reporting_headcount',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['reporting_headcount'],
    unit: 'count',
    temporalType: 'count',
  }),
  metricEntry({
    metricKey: 'operating_expense_per_head_usd',
    label: 'Operating expense per head',
    definition: 'Operating expense divided by reporting headcount.',
    reconciliationRule: 'Derived from approved operating expense and reporting headcount.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/operating_expense_per_head_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['operating_expense_per_head_usd'],
    unit: 'USD_per_head',
    frame: 'expense_per_head',
    temporalType: 'ratio',
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: RATIO_AGGREGATION_RULES,
    components: components(['operating_expense_usd'], ['reporting_headcount'], 'count'),
    measureIdentity: OPERATING_EXPENSE_PER_HEAD_MEASURE_IDENTITY,
  }),
  metricEntry({
    metricKey: 'objective_baseline_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Objective baseline',
    definition: 'Baseline for the configured finance objective metric.',
    reconciliationRule:
      'Read from the approved finance artifact and mapped through the active industry profile default objective metric.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/objective_baseline_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['objective_baseline_usd'],
  }),
  metricEntry({
    metricKey: 'objective_target_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Objective target',
    definition: 'Approved target for the configured finance objective metric.',
    reconciliationRule: 'Read from an approved StrategicObjectiveBinding for the active strategy.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/objective_target_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['objective_target_usd'],
  }),
  metricEntry({
    metricKey: 'objective_gap_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Objective value gap',
    definition: 'Approved target less governed baseline for the configured finance objective metric.',
    reconciliationRule:
      'Computed by objective_value_gap_v1; finance does not quantify lever closure.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/objective_gap_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['objective_gap_usd'],
    sourceOfTruthClass: 'derived',
    composedFrom: {
      formulaId: 'objective_value_gap_v1',
      formulaVersion: '1.0.1',
      inputMetricKeys: ['objective_baseline_usd', 'objective_target_usd'],
    },
    // Code-wins delta (PR body): evidence-design names objective_value_gap canonical, but
    // objective_gap_usd is the live metricKey; we keep it canonical and register
    // objective_value_gap as its exact alias so neither is both an id and an alias.
    aliases: ['objective_value_gap'],
    signConvention: { kind: 'lower_is_better', costLine: false },
  }),
  metricEntry({
    metricKey: 'objective_gap_pct',
    measureIdentity: { measuredDimension: 'other', valueForm: 'change', unitFamily: 'percent', normalizationBasis: 'other' },
    label: 'Objective value gap percent',
    definition: 'Objective value gap divided by the governed baseline.',
    reconciliationRule: 'Computed by objective_value_gap_v1 from the same baseline and target values.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_CANONICAL_METRIC_DEFINITIONS/objective_gap_pct',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['objective_gap_pct'],
    unit: 'percent',
    frame: 'objective_gap_pct',
    temporalType: 'ratio',
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: RATIO_AGGREGATION_RULES,
    components: components(['objective_gap_usd'], ['objective_baseline_usd']),
  }),
  metricEntry({
    metricKey: 'gross_revenue_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Gross revenue',
    definition: '3PL gross revenue from the approved accounting artifact.',
    reconciliationRule: 'Maps to canonical revenue.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_INDUSTRY_PROFILES/3pl-logistics/gross_revenue_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['gross_revenue_usd'],
    registryLayer: 'industry',
    industryProfileId: '3pl-logistics',
  }),
  metricEntry({
    metricKey: 'purchased_transportation_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    direction: 'lower_is_better',
    label: 'Purchased transportation',
    definition: '3PL purchased transportation cost of revenue.',
    reconciliationRule: 'Maps to canonical cost of revenue for the 3PL profile.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_INDUSTRY_PROFILES/3pl-logistics/purchased_transportation_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['purchased_transportation_usd'],
    registryLayer: 'industry',
    industryProfileId: '3pl-logistics',
  }),
  metricEntry({
    metricKey: 'net_revenue_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Net revenue',
    definition: '3PL gross profit equivalent: gross revenue less purchased transportation.',
    reconciliationRule: 'Maps to canonical gross profit and reconciles to revenue minus cost of revenue.',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_INDUSTRY_PROFILES/3pl-logistics/net_revenue_usd',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['net_revenue_usd'],
    registryLayer: 'industry',
    industryProfileId: '3pl-logistics',
  }),
  ...FINANCE_3PL_SUPPLEMENTAL_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry),
  ratioMetricEntry({
    metricKey: 'brokerage.net_revenue_margin_pct',
    measureIdentity: { measuredDimension: 'other', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'revenue' },
    label: 'Brokerage net revenue margin',
    definition: '3PL net revenue divided by gross revenue for brokerage-style reporting.',
    frame: 'margin_on_gross',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#profileMember:3pl-logistics.grossMargin',
    sourceBindingKeys: ['gross_profit_usd', 'revenue_usd'],
    numerator: ['gross_profit_usd'],
    denominator: ['revenue_usd'],
    appliesTo: [{ dimension: 'business_line', members: ['nast'] }],
  }),
  ratioMetricEntry({
    metricKey: 'forwarding.gross_margin_on_fee_pct',
    measureIdentity: { measuredDimension: 'other', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'other' },
    label: 'Forwarding gross margin on fee',
    definition:
      'Forwarding operating margin expressed on the fee / adjusted-gross-profit frame.',
    frame: 'margin_on_fee',
    source:
      'apps/api/src/decision-modules/finance/finance.profile.ts#profileMember:3pl-logistics.segmentReporting.operatingMargin',
    sourceBindingKeys: ['operating_income_usd', 'gross_profit_usd'],
    numerator: ['operating_income_usd'],
    denominator: ['gross_profit_usd'],
    appliesTo: [{ dimension: 'business_line', members: ['global_forwarding'] }],
  }),
  // --- Semantic ontology: governed objective-value-bridge target metrics (evidence-design §2.1) ---
  // `operating_income` and `objective_value_gap` targets resolve as aliases of
  // `operating_income_usd` / `objective_gap_usd` above. These two targets have no
  // pre-existing canonical metric, so they are registered here as derived roll-up anchors.
  metricEntry({
    metricKey: 'cash_flow_release',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Cash flow release',
    definition:
      'Governed cash-flow-release target that capital-allocation freed-capital contributions roll into via the objective-value bridge.',
    reconciliationRule:
      'Roll-up anchor recomputed by objective_value_bridge_v1 from its registered contribution inputs; never summed with the operating-income target.',
    // Re-pointed 2026-08-05 (#4449): the prior locator named
    // capex-discipline.engine.ts, retired as a stub. capital-allocation is the
    // registry's baseline anchor for this metric since #4468.
    source:
      'apps/api/src/decision-modules/capital-allocation/capital-allocation.engine.ts#impactMetricKey:cash_flow_release',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['cash_flow_release'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'strategic_execution_capacity_gap',
    measureIdentity: { measuredDimension: 'other', valueForm: 'change', unitFamily: 'count', normalizationBasis: 'none' },
    label: 'Strategic execution capacity gap',
    definition:
      'Governed capacity-gap target (in FTE) that the strategic-execution capacity diagnostic contribution rolls into.',
    reconciliationRule:
      'Roll-up anchor recomputed by strategic_execution_capacity_utilization; a capacity shortfall, not a dollar total — never summed with dollar targets.',
    source:
      'apps/api/src/decision-modules/strategic-execution-capacity-diagnostic/strategic-execution-capacity-diagnostic.manifest.ts#metricId:strategic_execution_capacity_gap',
    registryLayer: 'core',
    unit: 'fte',
    temporalType: 'count',
    direction: 'lower_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'management-estimate',
    ownerModuleId: 'strategic-execution-capacity-diagnostic',
    sourceBindingKeys: ['strategic_execution_capacity_gap'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'capacity_utilization_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Capacity Utilization Decision: capacity_utilization_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the capacity-utilization module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by capacity_utilization_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/capacity-utilization/capacity-utilization.engine.ts#contributionClaimId:capacity_utilization_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'capacity-utilization',
    sourceBindingKeys: ['capacity_utilization_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  // #5081 - REINSTATED (lead ruling 2026-08-28). These five lever contributions
  // existed as governed registry rows minted 2026-07-25 but had fallen out of the
  // compiled pack, so pack activation never visited them and they sat CANDIDATE
  // while every sibling went ACTIVE. Every field below is taken verbatim from the
  // rows already in the governed registry - nothing here is authored fresh - so
  // the loader's digest law matches the row it is meant to activate. Like their
  // siblings they are module_composite_contribution_v1 outputs: no aggregation
  // rules, never_blend, no appliesTo, which keeps them out of the semantic
  // layer's queryable surface and off the planner catalog.
  metricEntry({
    metricKey: 'capex_discipline_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label:
      'Capex Discipline Decision: capex_discipline_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the capex-discipline module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by capex_discipline_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/capex-discipline/capex-discipline.engine.ts#contributionClaimId:capex_discipline_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'capex-discipline',
    sourceBindingKeys: ['capex_discipline_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'growth_cross_sell_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label:
      'Cross-Sell Growth Decision: growth_cross_sell_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the growth-cross-sell module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by growth_cross_sell_addressable_share in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/growth-cross-sell/growth-cross-sell.engine.ts#contributionClaimId:growth_cross_sell_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'growth-cross-sell',
    sourceBindingKeys: ['growth_cross_sell_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'mode_mix_shift_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label:
      'Mode Mix Shift Decision: mode_mix_shift_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the mode-mix-shift module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by mode_mix_shift_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/mode-mix-shift/mode-mix-shift.engine.ts#contributionClaimId:mode_mix_shift_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'mode-mix-shift',
    sourceBindingKeys: ['mode_mix_shift_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'revenue_mix_shift_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label:
      'Revenue Mix Shift Decision: revenue_mix_shift_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the revenue-mix-shift module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by revenue_mix_shift_addressable_share in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/revenue-mix-shift/revenue-mix-shift.engine.ts#contributionClaimId:revenue_mix_shift_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'revenue-mix-shift',
    sourceBindingKeys: ['revenue_mix_shift_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'working_capital_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label:
      'Working Capital Efficiency Decision: working_capital_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the working-capital module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by working_capital_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/working-capital/working-capital.engine.ts#contributionClaimId:working_capital_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'working-capital',
    sourceBindingKeys: ['working_capital_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  ...FINANCE_LEVER_CONTRIBUTION_METRIC_REGISTRY_SEED_INPUTS_A.map(metricEntry),
  metricEntry({
    metricKey: 'transportation_margin_delta_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Transportation Cost & Margin Analysis: transportation_margin_delta_usd',
    definition:
      'Governed decision-lever contribution the carrier-transportation-cost module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by the approved formula customer_repricing_volume_response_v1, executed by the size_customer_repricing_volume_response operation in the owning module; lane-level and elasticity-driven, distinct from demand_revenue_repricing_v1 and freight_cost_repricing_v1 and never summed with either; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.manifest.ts#contributionClaimId:transportation_margin_delta_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'carrier-transportation-cost',
    sourceBindingKeys: ['transportation_margin_delta_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'cts_recovery_opportunity_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Cost to Serve Decision: cts_recovery_opportunity_usd',
    definition:
      'Governed decision-lever contribution the cost-to-serve module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by cts_recovery_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/cost-to-serve/cost-to-serve.manifest.ts#contributionClaimId:cts_recovery_opportunity_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'cost-to-serve',
    sourceBindingKeys: ['cts_recovery_opportunity_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'cts_reprice_delta_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Cost to Serve Decision: cts_reprice_delta_usd',
    definition:
      'Governed decision-lever contribution the cost-to-serve module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by cts_reprice_delta in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/cost-to-serve/cost-to-serve.manifest.ts#contributionClaimId:cts_reprice_delta_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'cost-to-serve',
    sourceBindingKeys: ['cts_reprice_delta_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'cts_service_redesign_delta_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Cost to Serve Decision: cts_service_redesign_delta_usd',
    definition:
      'Governed decision-lever contribution the cost-to-serve module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by cts_service_redesign in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/cost-to-serve/cost-to-serve.manifest.ts#contributionClaimId:cts_service_redesign_delta_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'cost-to-serve',
    sourceBindingKeys: ['cts_service_redesign_delta_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'fuel_efficiency_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Fuel and Accessorial Efficiency Decision: fuel_efficiency_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the fuel-efficiency module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by fuel_efficiency_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/fuel-efficiency/fuel-efficiency.engine.ts#contributionClaimId:fuel_efficiency_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'fuel-efficiency',
    sourceBindingKeys: ['fuel_efficiency_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'labor_arbitrage_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Labor Arbitrage Decision: labor_arbitrage_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the labor-arbitrage module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by labor_arbitrage_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/labor-arbitrage/labor-arbitrage.engine.ts#contributionClaimId:labor_arbitrage_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'labor-arbitrage',
    sourceBindingKeys: ['labor_arbitrage_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'marketing_spend_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Marketing and Demand Generation Consolidation Decision: marketing_spend_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the marketing-spend module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by marketing_spend_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/marketing-spend/marketing-spend.engine.ts#contributionClaimId:marketing_spend_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'marketing-spend',
    sourceBindingKeys: ['marketing_spend_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'network_optimization_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Network Optimization Decision: network_optimization_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the network-optimization module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by network_optimization_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/network-optimization/network-optimization.engine.ts#contributionClaimId:network_optimization_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'network-optimization',
    sourceBindingKeys: ['network_optimization_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'delayering_net_ebitda_opportunity_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Org Analysis Optimization Decision: delayering_net_ebitda_opportunity_usd',
    definition:
      'Governed decision-lever contribution the org-analysis-optimization module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by delayering_net_ebitda_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/org-analysis-optimization/org-analysis-optimization.manifest.ts#metricKey:delayering_net_ebitda_opportunity_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'org-analysis-optimization',
    sourceBindingKeys: ['delayering_net_ebitda_opportunity_usd'],
    // named gap: module declares no targetMetricRef in the audit book — left undeclared, not invented.
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'org_cost_sub_lever_net_run_rate_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Org Analysis Optimization Decision: org_cost_sub_lever_net_run_rate_contribution_usd',
    definition:
      'Governed decision-lever contribution the org-analysis-optimization module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by source_owned_org_cost_sub_lever_buildup in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/org-analysis-optimization/org-analysis-optimization.intent-catalog.ts#contributionClaimId:org_cost_sub_lever_net_run_rate_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'org-analysis-optimization',
    sourceBindingKeys: ['org_cost_sub_lever_net_run_rate_contribution_usd'],
    blendPolicy: { kind: 'additive' },
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'outside_contractors_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Outside Contractors Decision: outside_contractors_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the outside-contractors module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by outside_contractors_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/outside-contractors/outside-contractors.engine.ts#contributionClaimId:outside_contractors_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'outside-contractors',
    sourceBindingKeys: ['outside_contractors_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'pricing_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Pricing Realization Decision: pricing_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the pricing-realization module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by pricing_realization_oi_contribution in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/pricing-realization/pricing-realization.manifest.ts#contributionClaimId:pricing_operating_income_contribution_usd',
    registryLayer: 'industry',
    industryProfileId: '3pl-logistics',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'pricing-realization',
    sourceBindingKeys: ['pricing_operating_income_contribution_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'pvm_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Pricing Realization Decision: pvm_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the pricing-realization module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by price_volume_mix_bridge in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/pricing-realization/pricing-realization.manifest.ts#contributionClaimId:pvm_operating_income_contribution_usd',
    registryLayer: 'industry',
    industryProfileId: '3pl-logistics',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'pricing-realization',
    sourceBindingKeys: ['pvm_operating_income_contribution_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'scenario_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Pricing Realization Decision: scenario_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the pricing-realization module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by pricing_increase_elasticity_scenario in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/pricing-realization/pricing-realization.manifest.ts#contributionClaimId:scenario_operating_income_contribution_usd',
    registryLayer: 'industry',
    industryProfileId: '3pl-logistics',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'pricing-realization',
    sourceBindingKeys: ['scenario_operating_income_contribution_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'productivity_opportunity_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Productivity / Lean-AI Realization Decision: productivity_opportunity_usd',
    definition:
      'Governed decision-lever contribution the productivity-lean-ai module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by productivity_oi_conversion in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/productivity-lean-ai/productivity-lean-ai.manifest.ts#contributionClaimId:productivity_opportunity_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'productivity-lean-ai',
    sourceBindingKeys: ['productivity_opportunity_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'professional_fees_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Professional Fees Rationalization Decision: professional_fees_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the professional-fees module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by professional_fees_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/professional-fees/professional-fees.engine.ts#contributionClaimId:professional_fees_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'professional-fees',
    sourceBindingKeys: ['professional_fees_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'real_estate_footprint_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Real Estate and Facilities Decision: real_estate_footprint_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the real-estate-footprint module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by real_estate_footprint_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/real-estate-footprint/real-estate-footprint.engine.ts#contributionClaimId:real_estate_footprint_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'real-estate-footprint',
    sourceBindingKeys: ['real_estate_footprint_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'claims_reduction_opportunity_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Risk & Claims Decision: claims_reduction_opportunity_usd',
    definition:
      'Governed decision-lever contribution the risk-claims module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by claims_reduction_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/risk-claims/risk-claims.manifest.ts#contributionClaimId:claims_reduction_opportunity_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'risk-claims',
    sourceBindingKeys: ['claims_reduction_opportunity_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'segment_cleanup_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Segment / Corporate Margin Cleanup Decision: segment_cleanup_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the segment-margin-cleanup module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by segment_cleanup_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/segment-margin-cleanup/segment-margin-cleanup.manifest.ts#contributionClaimId:segment_cleanup_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'segment-margin-cleanup',
    sourceBindingKeys: ['segment_cleanup_operating_income_contribution_usd', 'segment_cleanup_opportunity_usd'],
    aliases: ['segment_cleanup_opportunity_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'sga_spend_total_opportunity_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'SGA Spend Optimization Decision: sga_spend_total_opportunity_usd',
    definition:
      'Governed decision-lever contribution the sga-spend module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by professional_fees_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/sga-spend/sga-spend.manifest.ts#contributionClaimId:sga_spend_total_opportunity_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'sga-spend',
    sourceBindingKeys: ['sga_spend_total_opportunity_usd'],
    blendPolicy: { kind: 'additive' },
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'indirect_procurement_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Indirect procurement operating income contribution',
    definition:
      'Governed indirect-procurement contribution emitted by the SGA spend lever catalog.',
    reconciliationRule:
      'The source lever owns the numeric binding; no rollup or approved composition edge is inferred.',
    source:
      'apps/api/src/decision-modules/sga-spend/sga-spend.types.ts#contributionClaimId:indirect_procurement_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'sga-spend',
    sourceBindingKeys: ['indirect_procurement_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'tne_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Travel and expense operating income contribution',
    definition: 'Governed travel-and-expense contribution emitted by the SGA spend lever catalog.',
    reconciliationRule:
      'The source lever owns the numeric binding; no rollup or approved composition edge is inferred.',
    source:
      'apps/api/src/decision-modules/sga-spend/sga-spend.types.ts#contributionClaimId:tne_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'sga-spend',
    sourceBindingKeys: ['tne_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'shared_services_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Shared Services Consolidation Decision: shared_services_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the shared-services module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by shared_services_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/shared-services/shared-services.engine.ts#contributionClaimId:shared_services_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'shared-services',
    sourceBindingKeys: ['shared_services_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'capacity_gap_fte',
    measureIdentity: { measuredDimension: 'other', valueForm: 'change', unitFamily: 'count', normalizationBasis: 'none' },
    label: 'Strategic Execution & Capacity: capacity_gap_fte',
    definition:
      'Governed decision-lever contribution the strategic-execution-capacity-diagnostic module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by strategic_execution_capacity_utilization in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/strategic-execution-capacity-diagnostic/strategic-execution-capacity-diagnostic.manifest.ts#contributionClaimId:capacity_gap_fte',
    registryLayer: 'core',
    unit: 'fte',
    temporalType: 'count',
    direction: 'lower_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'strategic-execution-capacity-diagnostic',
    sourceBindingKeys: ['capacity_gap_fte'],
    targetMetricRef: { metricId: 'strategic_execution_capacity_gap', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'tech_software_operating_income_contribution_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Technology and Software Rationalization Decision: tech_software_operating_income_contribution_usd',
    definition:
      'Governed decision-lever contribution the tech-software module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
    reconciliationRule:
      'Recomputed by tech_software_opportunity in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
    source:
      'apps/api/src/decision-modules/tech-software/tech-software.engine.ts#contributionClaimId:tech_software_operating_income_contribution_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'tech-software',
    sourceBindingKeys: ['tech_software_operating_income_contribution_usd'],
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  metricEntry({
    metricKey: 'winrate_uplift_oi_usd',
    measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'Commercial Effectiveness Decision: winrate_uplift_oi_usd',
    definition:
      'Governed decision-lever contribution the commercial-effectiveness module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, #4009 wave-2C contract census 2026-07-27).',
    reconciliationRule:
      'Recomputed by winrate_uplift_v1 in the owning module; rolls into its target only through the objective-value bridge, never summed twice (candidate_only: growth uplift stays out of flat-revenue bridges without shared-scope receipts).',
    source:
      'apps/api/src/decision-modules/commercial-effectiveness/commercial-effectiveness.manifest.ts#contributionClaimId:winrate_uplift_oi_usd',
    registryLayer: 'core',
    unit: 'USD',
    temporalType: 'flow',
    direction: 'higher_is_better',
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'commercial-effectiveness',
    sourceBindingKeys: ['winrate_uplift_oi_usd'],
    targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
    requiredEvidenceClasses: ['module_composite_contribution_v1'],
  }),
  ...WORKFORCE_REDUCTION_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry),
  metricEntry({
    // Codex D-F ruling, issue #4631 comment 5307297438 (Gap 2 — metric registry
    // identity): a DISTINCT governed identity for the committed cost-line drift
    // seed, extracted from that seed script (extract-do-not-invent: METRIC_KEY,
    // METRIC_LABEL, METRIC_UNIT and the committed $92.4M/yr -> $84.0M/yr
    // decrease all come from the cited source). Load-bearing corrections:
    //   - unit is usd_per_year, NOT generic USD — the shared unit authority
    //     (governed-unit-semantics.ts) compares unnamed units exactly and does
    //     not equate USD with usd_per_year, so a generic-USD definition would
    //     block the drift-catalog objective on governed_metric_unit_mismatch;
    //   - direction is lower_is_better (the committed plan DECREASES the rate);
    //   - temporal semantics are an annualized run-rate LEVEL observed at an
    //     instant (stock), never a period flow to be summed across periods;
    //   - never_blend with no aggregation rules: no separately governed
    //     aggregation rule exists for this cost line;
    //   - NO aliases: the ruling forbids aliasing to operating_expense_usd,
    //     EBITDA, or any broader finance measure.
    // Registering this identity closes ONLY the named identity gap. It does NOT
    // make modeled artifacts admissible — the drift catalog source-class fence
    // still blocks activation after identity resolution succeeds (Gap 1,
    // option (b) of the same ruling).
    metricKey: 'logistics_overhead_run_rate_usd',
    measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'other', normalizationBasis: 'none' },
    label: 'Logistics overhead run-rate',
    definition:
      'Annualized logistics-overhead run-rate: the yearly pace of logistics overhead ' +
      'spend in USD per year, observed as a level at a point in time. Not a period ' +
      'expense and not an EBITDA contribution.',
    reconciliationRule:
      'Resolves from the approved governed data-lake artifact carrying ' +
      'signals.logistics_overhead_run_rate_usd for the monitored strategy scope; ' +
      'weekly observations are run-rate levels and are never summed across periods.',
    source:
      'apps/api/src/scripts/drift-demo/seed-demo-cost-line-drift.ts#const:METRIC_KEY',
    registryLayer: 'core',
    unit: 'usd_per_year',
    frame: 'annualized_run_rate',
    temporalType: 'stock',
    direction: 'lower_is_better',
    signConvention: { kind: 'lower_is_better', costLine: true },
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: [],
    appliesTo: [],
    benchmarkable: false,
    sourceOfTruthClass: 'management-estimate',
    ownerModuleId: 'finance',
    sourceBindingKeys: ['logistics_overhead_run_rate_usd'],
    requiredEvidenceClasses: ['logistics_overhead_governed_snapshot_v1'],
  }),
  // #5544 - the SG&A cost-pool canonical + basis measures. Inlined here (not via
  // the SGA_COST_POOL_METRIC_REGISTRY_SEED const below) because this array is a
  // flat literal evaluated top-to-bottom at module load and that const is
  // declared later in the file - referencing it here would be a TDZ error.
  // Declaration data lives in the focused leaf metric-registry.sga-cost-pool.ts
  // (shrink-on-touch); this is a mechanical map, not a second declaration.
  ...SGA_COST_POOL_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry),
];

const CONTRIBUTION_COMPOSITION_SOURCE_REFS = Object.freeze({
  capacity_utilization_operating_income_contribution_usd: ['capacity_utilization_opportunity'],
  // #4009 wave-2C: capital-allocation.manifest.ts ObjectiveContributionContract formulaRefs.
  capital_freed_usd: ['capex_reprioritization_value_v1'],
  carrier_cost_opportunity_usd: ['carrier_transportation_cost_opportunity'],
  carrier_exit_net_margin_impact_usd: ['carrier_exit_tradeoff_v1'],
  churn_reduction_operating_income_contribution_usd: ['churn_reduction_opportunity'],
  mode_shift_margin_impact_usd: ['mode_shift_tradeoff_v1'],
  network_reconfiguration_margin_impact_usd: ['specialist_network_option_score_v1'],
  transportation_margin_delta_usd: ['customer_repricing_volume_response_v1'],
  cts_recovery_opportunity_usd: ['cts_recovery_opportunity_v1'],
  cts_reprice_delta_usd: ['cts_reprice_delta_v1'],
  cts_service_redesign_delta_usd: ['cts_service_redesign_v1'],
  fuel_efficiency_operating_income_contribution_usd: ['fuel_efficiency_opportunity'],
  labor_arbitrage_operating_income_contribution_usd: ['labor_arbitrage_opportunity'],
  marketing_spend_operating_income_contribution_usd: ['marketing_spend_opportunity'],
  network_optimization_operating_income_contribution_usd: ['network_optimization_opportunity'],
  delayering_net_ebitda_opportunity_usd: ['delayering_net_ebitda_opportunity@1.0.0'],
  org_cost_sub_lever_net_run_rate_contribution_usd: [
    'org_cost_sub_lever_net_run_rate_contribution_v1',
  ],
  outside_contractors_operating_income_contribution_usd: ['outside_contractors_opportunity'],
  pricing_operating_income_contribution_usd: ['pricing_realization_oi_contribution_v1'],
  pvm_operating_income_contribution_usd: ['price_volume_mix_bridge_v1'],
  scenario_operating_income_contribution_usd: ['pricing_increase_elasticity_scenario_v1'],
  productivity_opportunity_usd: ['productivity_oi_conversion'],
  professional_fees_operating_income_contribution_usd: ['professional_fees_opportunity'],
  real_estate_footprint_operating_income_contribution_usd: ['real_estate_footprint_opportunity'],
  claims_reduction_opportunity_usd: ['claims_reduction_opportunity_v1'],
  segment_cleanup_operating_income_contribution_usd: ['segment_cleanup_opportunity'],
  sga_spend_total_opportunity_usd: [
    'professional_fees_opportunity',
    'outside_contractors_opportunity',
    'indirect_procurement_opportunity',
    'tne_leakage_opportunity',
    'shared_services_opportunity',
    'tech_software_opportunity',
    'real_estate_footprint_opportunity',
    'marketing_spend_opportunity',
    'sga_spend_recovery_factor_scenario_v1',
  ],
  indirect_procurement_operating_income_contribution_usd: [
    'indirect_procurement_opportunity@1.0.0',
  ],
  tne_operating_income_contribution_usd: ['tne_leakage_opportunity@1.0.0'],
  shared_services_operating_income_contribution_usd: ['shared_services_opportunity'],
  capacity_gap_fte: ['strategic_execution_capacity_utilization@1.0.0'],
  tech_software_operating_income_contribution_usd: ['tech_software_opportunity'],
  // #4009 wave-2C: commercial-effectiveness.manifest.ts ObjectiveContributionContract formulaRefs.
  winrate_uplift_oi_usd: ['winrate_uplift_v1'],
  workforce_reduction_net_ebitda_opportunity_usd: [
    'workforce_reduction_net_ebitda_opportunity@1.0.0',
  ],
} satisfies Readonly<Record<string, readonly string[]>>);

const CONTRIBUTION_APPROVED_FORMULA_REFS = Object.freeze({
  capacity_utilization_operating_income_contribution_usd: {
    formulaId: 'capacity_utilization_opportunity',
    formulaVersion: '1.0.0',
  },
  carrier_cost_opportunity_usd: {
    formulaId: 'carrier_transportation_cost_opportunity',
    formulaVersion: '1.0.3',
  },
  // SPEC-1..4 of the #4008 CHW park: the four carrier scenario claims now bind
  // to real approved formulas instead of naming a deterministic operation id.
  carrier_exit_net_margin_impact_usd: {
    formulaId: 'carrier_exit_tradeoff_v1',
    formulaVersion: '1.0.0',
  },
  mode_shift_margin_impact_usd: {
    formulaId: 'mode_shift_tradeoff_v1',
    formulaVersion: '1.0.0',
  },
  network_reconfiguration_margin_impact_usd: {
    formulaId: 'specialist_network_option_score_v1',
    formulaVersion: '1.0.0',
  },
  transportation_margin_delta_usd: {
    formulaId: 'customer_repricing_volume_response_v1',
    formulaVersion: '1.0.0',
  },
  churn_reduction_operating_income_contribution_usd: {
    formulaId: 'churn_reduction_opportunity',
    formulaVersion: '1.0.0',
  },
  cts_recovery_opportunity_usd: {
    formulaId: 'cts_recovery_opportunity_v1',
    formulaVersion: '1.0.0',
  },
  cts_reprice_delta_usd: {
    formulaId: 'cts_reprice_delta_v1',
    formulaVersion: '1.0.0',
  },
  cts_service_redesign_delta_usd: {
    formulaId: 'cts_service_redesign_v1',
    formulaVersion: '1.0.0',
  },
  fuel_efficiency_operating_income_contribution_usd: {
    formulaId: 'fuel_efficiency_opportunity',
    formulaVersion: '1.0.0',
  },
  labor_arbitrage_operating_income_contribution_usd: {
    formulaId: 'labor_arbitrage_opportunity',
    formulaVersion: '1.0.0',
  },
  marketing_spend_operating_income_contribution_usd: {
    formulaId: 'marketing_spend_opportunity',
    formulaVersion: '1.0.0',
  },
  network_optimization_operating_income_contribution_usd: {
    formulaId: 'network_optimization_opportunity',
    formulaVersion: '1.0.0',
  },
  delayering_net_ebitda_opportunity_usd: {
    formulaId: 'delayering_net_ebitda_opportunity',
    formulaVersion: '1.0.0',
  },
  org_cost_sub_lever_net_run_rate_contribution_usd: {
    formulaId: 'org_cost_sub_lever_net_run_rate_contribution_v1',
    formulaVersion: '1.0.0',
  },
  outside_contractors_operating_income_contribution_usd: {
    formulaId: 'outside_contractors_opportunity',
    formulaVersion: '1.0.0',
  },
  pricing_operating_income_contribution_usd: {
    formulaId: 'pricing_realization_oi_contribution_v1',
    formulaVersion: '1.0.0',
  },
  pvm_operating_income_contribution_usd: {
    formulaId: 'price_volume_mix_bridge_v1',
    formulaVersion: '1.0.0',
  },
  scenario_operating_income_contribution_usd: {
    formulaId: 'pricing_increase_elasticity_scenario_v1',
    formulaVersion: '1.0.0',
  },
  productivity_opportunity_usd: {
    formulaId: 'productivity_oi_conversion',
    formulaVersion: '1.0.0',
  },
  professional_fees_operating_income_contribution_usd: {
    formulaId: 'professional_fees_opportunity',
    formulaVersion: '1.0.0',
  },
  real_estate_footprint_operating_income_contribution_usd: {
    formulaId: 'real_estate_footprint_opportunity',
    formulaVersion: '1.0.0',
  },
  claims_reduction_opportunity_usd: {
    formulaId: 'claims_reduction_opportunity_v1',
    formulaVersion: '1.0.0',
  },
  segment_cleanup_operating_income_contribution_usd: {
    formulaId: 'segment_cleanup_opportunity',
    formulaVersion: '1.0.0',
  },
  indirect_procurement_operating_income_contribution_usd: {
    formulaId: 'indirect_procurement_opportunity',
    formulaVersion: '1.0.0',
  },
  tne_operating_income_contribution_usd: {
    formulaId: 'tne_leakage_opportunity',
    formulaVersion: '1.0.0',
  },
  shared_services_operating_income_contribution_usd: {
    formulaId: 'shared_services_opportunity',
    formulaVersion: '1.0.0',
  },
  capacity_gap_fte: {
    formulaId: 'strategic_execution_capacity_utilization',
    formulaVersion: '1.0.0',
  },
  tech_software_operating_income_contribution_usd: {
    formulaId: 'tech_software_opportunity',
    formulaVersion: '1.0.0',
  },
  // #4009 wave-2C: capital-allocation contract formula, approved at 1.0.0
  // (approved-formulas.ts capex_reprioritization_value_v1).
  capital_freed_usd: {
    formulaId: 'capex_reprioritization_value_v1',
    formulaVersion: '1.0.0',
  },
  // #4009 wave-2C: commercial-effectiveness contract formula, approved at 1.0.0
  // (approved-formulas.ts winrate_uplift_v1).
  winrate_uplift_oi_usd: {
    formulaId: 'winrate_uplift_v1',
    formulaVersion: '1.0.0',
  },
} satisfies Readonly<
  Record<string, Readonly<{ formulaId: string; formulaVersion: string }>>
>);

export const METRIC_REGISTRY_COMPOSITION_GAPS: readonly MetricRegistryCompositionGap[] =
  Object.freeze(
    Object.entries(CONTRIBUTION_COMPOSITION_SOURCE_REFS)
      .map(([metricKey, sourceFormulaRefs]) => {
        const metric = FINANCE_SEMANTIC_LAYER_REGISTRY_SEED.find(
          (entry) => entry.metricKey === metricKey,
        );
        if (!metric) {
          throw new Error(`Composition-gap metric is not registered: ${metricKey}`);
        }
        const approvedFormulaRef = CONTRIBUTION_APPROVED_FORMULA_REFS[metricKey as keyof typeof CONTRIBUTION_APPROVED_FORMULA_REFS];
        return Object.freeze({
          metricKey,
          sourceFormulaRefs: Object.freeze([...sourceFormulaRefs]),
          source: metric.source,
          ...(approvedFormulaRef
            ? {
                approvedFormulaRef: Object.freeze({ ...approvedFormulaRef }),
                reason: 'composition_inputs_not_registered' as const,
              }
            : { reason: 'approved_formula_not_registered' as const }),
        });
      })
      .sort((left, right) => left.metricKey.localeCompare(right.metricKey)),
  );

function versionedMetricDefinition(
  metricKey: string,
  input: Readonly<{
    aliases: readonly string[];
    source: string;
    sourceBindingKeys: readonly string[];
    ownerModuleId: string;
  }>,
): MetricRegistryEntry {
  const canonical = FINANCE_SEMANTIC_LAYER_REGISTRY_SEED.find(
    (entry) => entry.metricKey === metricKey,
  );
  if (!canonical) {
    throw new Error(`Objective-value-bridge metric has no canonical definition: ${metricKey}`);
  }
  return Object.freeze({
    ...canonical,
    registryVersion: 'objective_value_bridge_v1',
    aliases: Object.freeze([...input.aliases]),
    source: input.source,
    sourceBindingKeys: Object.freeze([...input.sourceBindingKeys]),
    ownerModuleId: input.ownerModuleId,
  });
}

export const OBJECTIVE_VALUE_BRIDGE_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] =
  Object.freeze([
    versionedMetricDefinition('operating_income_usd', {
      aliases: ['operating_income'],
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.manifest.ts#metricId:operating_income',
      sourceBindingKeys: ['operating_income'],
      ownerModuleId: 'finance',
    }),
    versionedMetricDefinition('objective_gap_usd', {
      aliases: ['objective_value_gap'],
      source:
        'apps/api/src/decision-modules/finance/finance.manifest.ts#metricId:objective_value_gap',
      sourceBindingKeys: ['objective_value_gap'],
      ownerModuleId: 'finance',
    }),
    versionedMetricDefinition('cash_flow_release', {
      aliases: [],
      source:
        'apps/api/src/decision-modules/capital-allocation/capital-allocation.engine.ts#impactMetricKey:cash_flow_release',
      sourceBindingKeys: ['cash_flow_release'],
      ownerModuleId: 'finance',
    }),
    versionedMetricDefinition('strategic_execution_capacity_gap', {
      aliases: [],
      source:
        'apps/api/src/decision-modules/strategic-execution-capacity-diagnostic/strategic-execution-capacity-diagnostic.manifest.ts#metricId:strategic_execution_capacity_gap',
      sourceBindingKeys: ['strategic_execution_capacity_gap'],
      ownerModuleId: 'strategic-execution-capacity-diagnostic',
    }),
  ]);

export const MA_SYNERGY_TRACKING_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] = Object.freeze(MA_SYNERGY_TRACKING_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry));
// #5544 - the SG&A cost-pool canonical + basis measures; declaration data lives
// in the focused leaf metric-registry.sga-cost-pool.ts (shrink-on-touch), same
// seed-input/`.map(metricEntry)` split MA-synergy-tracking established above.
export const SGA_COST_POOL_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] = Object.freeze(SGA_COST_POOL_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry));
export const MA_DEAL_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] = Object.freeze(MA_DEAL_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry));
// #5741 D3 ruling: the three source-grain SG&A spend detail keys, registered
// once the generic contract landed (#5733 rework). See
// metric-registry.sga-spend-source.data.ts for why exactly these three.
export const SGA_SPEND_SOURCE_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] = Object.freeze(SGA_SPEND_SOURCE_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry));

export const GL_DATA_LAKE_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] = Object.freeze(
  GL_DATA_LAKE_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry),
);
// #5940 — the five industry-benchmark-lens output keys the module already
// emits (`industry-benchmark-lens.module-definition.ts`), registered so the
// manager runtime's answerAnchors -> compiled-registry lookup resolves them.
// See metric-registry.industry-benchmark-lens.data.ts for the full defect trace.
export const INDUSTRY_BENCHMARK_LENS_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] = Object.freeze(INDUSTRY_BENCHMARK_LENS_METRIC_REGISTRY_SEED_INPUTS.map(metricEntry));

// Added 2026-08-28 (helix_retail modeled data pack). Closes the compiled-registry half
// of the `metric_registry_missing` keys (see docs/data/helix-retail-modeled-pack-20260828.md
// and LOAD-PLAN.md); every `source` below names the exact emitting code.
export const HELIX_RETAIL_MODELED_PACK_REGISTRY_SEED_20260828: readonly MetricRegistryEntry[] =
  Object.freeze([
    // --- 5C carrier-transportation-cost — 9 keys, 0/9 previously compiled -------------
    metricEntry({
      metricKey: 'transportation_carrier_count',
      measureIdentity: { measuredDimension: 'other', valueForm: 'count', unitFamily: 'count', normalizationBasis: 'none' },
      label: 'Distinct carriers',
      definition: 'Count of distinct carriers present in the approved shipment-lane rows.',
      reconciliationRule: 'Direct count of distinct carrier values across approved shipment rows.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_carrier_count',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'count',
      temporalType: 'count',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_carrier_count'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_cost_per_mile_outlier_count',
      measureIdentity: { measuredDimension: 'other', valueForm: 'count', unitFamily: 'count', normalizationBasis: 'none' },
      label: 'Cost-per-mile outlier lanes',
      definition:
        'Count of approved shipment rows whose cost-per-mile falls outside the governed outlier band around the weighted benchmark.',
      reconciliationRule: 'Direct count from the cost-per-mile benchmark diagnostic.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_cost_per_mile_outlier_count',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'count',
      temporalType: 'count',
      direction: 'lower_is_better',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_cost_per_mile_outlier_count'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_lane_count',
      measureIdentity: { measuredDimension: 'other', valueForm: 'count', unitFamily: 'count', normalizationBasis: 'none' },
      label: 'Shipment lanes',
      definition: 'Count of approved shipment-lane rows in the governed carrier-transportation snapshot.',
      reconciliationRule: 'Direct row count of the approved shipment_lane_spend evidence.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_lane_count',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'count',
      temporalType: 'count',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_lane_count'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_top_carrier_spend_share',
      measureIdentity: { measuredDimension: 'other', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'other' },
      label: 'Top carrier spend share',
      definition: 'Largest single carrier share of total approved carrier spend.',
      reconciliationRule: 'Derived ratio: top carrier cost divided by total carrier cost, same reporting cut.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_top_carrier_spend_share',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'percent',
      frame: 'concentration_share_pct',
      temporalType: 'ratio',
      direction: 'lower_is_better',
      blendPolicy: { kind: 'never_blend' },
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_top_carrier_spend_share'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_total_carrier_cost_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Total carrier cost',
      definition: 'Sum of approved carrier cost across all shipment-lane rows for the reporting cut.',
      reconciliationRule: 'Sum-of-parts across approved shipment_lane_spend rows.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_total_carrier_cost_usd',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'USD',
      direction: 'lower_is_better',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_total_carrier_cost_usd'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_total_contract_variance_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Total contract rate variance',
      definition:
        'Sum of (carrier cost minus contracted carrier cost) across approved shipment-lane rows carrying a contracted rate.',
      reconciliationRule: 'Sum-of-parts across approved rows with a non-null contractedCarrierCostUsd.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_total_contract_variance_usd',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'USD',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_total_contract_variance_usd'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_total_margin_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Total transportation margin',
      definition: 'Sum of (revenue minus carrier cost) across approved shipment-lane rows for the reporting cut.',
      reconciliationRule: 'Sum-of-parts: transportation_total_revenue_usd minus transportation_total_carrier_cost_usd.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_total_margin_usd',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'USD',
      direction: 'higher_is_better',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_total_margin_usd'],
      components: components(['transportation_total_revenue_usd'], ['transportation_total_carrier_cost_usd']),
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_total_revenue_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Total transportation revenue',
      definition: 'Sum of approved shipment revenue across all shipment-lane rows for the reporting cut.',
      reconciliationRule: 'Sum-of-parts across approved shipment_lane_spend rows.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_total_revenue_usd',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'USD',
      direction: 'higher_is_better',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_total_revenue_usd'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'transportation_weighted_cost_per_mile_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'rate', unitFamily: 'other', normalizationBasis: 'other' },
      label: 'Weighted cost per mile',
      definition: 'Total approved carrier cost divided by total approved distance miles for the reporting cut.',
      reconciliationRule: 'Derived ratio (derived_ratio): transportation_total_carrier_cost_usd over summed distanceMiles.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.engine.ts#metricKey:transportation_weighted_cost_per_mile_usd',
      ownerModuleId: 'carrier-transportation-cost',
      unit: 'USD per mile',
      frame: 'cost_per_mile_usd',
      temporalType: 'ratio',
      direction: 'lower_is_better',
      blendPolicy: { kind: 'never_blend' },
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['transportation_weighted_cost_per_mile_usd'],
      benchmarkable: false,
    }),
    // #5544 shrink-on-touch: relocated verbatim to metric-registry.sga-spend-existing.data.ts
    // (was the "sga-spend lever sub-modules - 17 keys" + "sga-spend module aggregate - 13
    // keys" sections, 250 lines, 0 values changed - digest-parity proved in
    // sga-spend-existing-block-digest-parity.spec.ts, apps/api/src/semantic-layer).
    ...SGA_SPEND_EXISTING_REGISTRY_SEED_INPUTS.map(metricEntry),
    // --- finance derived gap/target/trend — 12 keys, 0/12 previously compiled -------
    // #5734 measure-identity sweep + #5549 target-comparison anchor: the 5th tuple
    // element carries the governed identity per key (dimension/valueForm/unitFamily/
    // normalizationBasis) since these rows are template-generated and have no static
    // `metricKey: '...'` literal to declare identity on directly. The 6th element is
    // the TARGET-COMPARISON ANCHOR: the governed base measure a gap metric states a
    // distance ON. It is DECLARED per key, never inferred from a `_gap` suffix, and
    // it is null for every key that states a level, a target or a trend rather than
    // a distance to an approved target.
    ...(
      [
        ['contracted_rate_variance_pct', 'Contracted rate variance', 'percent', 'lower_is_better',
          { measuredDimension: 'other', valueForm: 'change', unitFamily: 'percent', normalizationBasis: 'other' }, null] as const,
        ['d_and_a_yoy_delta_usd', 'D&A year-over-year delta', 'USD', 'context',
          { measuredDimension: 'other', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' }, null] as const,
        ['ebitda_gap_pct', 'EBITDA gap', 'percent', 'lower_is_better',
          { measuredDimension: 'other', valueForm: 'change', unitFamily: 'percent', normalizationBasis: 'other' }, 'ebitda_usd'] as const,
        ['ebitda_gap_usd', 'EBITDA gap', 'USD', 'lower_is_better',
          { measuredDimension: 'other', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' }, 'ebitda_usd'] as const,
        ['ebitda_target_usd', 'EBITDA target', 'USD', 'context',
          { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' }, null] as const,
        ['ebitda_yoy_delta_usd', 'EBITDA year-over-year delta', 'USD', 'context',
          { measuredDimension: 'other', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' }, null] as const,
        ['finance_momentum_projection_points', 'Finance momentum projection', 'points', 'context',
          { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'other', normalizationBasis: 'none' }, null] as const,
        ['income_from_operations_trend_total_pct', 'Income from operations trend (total)', 'percent', 'context',
          { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'percent', normalizationBasis: 'none' }, null] as const,
        ['income_from_operations_yoy_delta_usd', 'Income from operations year-over-year delta', 'USD', 'context',
          { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' }, null] as const,
        ['operating_income_gap_pct', 'Operating income gap', 'percent', 'lower_is_better',
          { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'percent', normalizationBasis: 'none' }, 'operating_income_usd'] as const,
        ['operating_income_gap_usd', 'Operating income gap', 'USD', 'lower_is_better',
          { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' }, 'operating_income_usd'] as const,
        ['operating_income_target_usd', 'Operating income target', 'USD', 'context',
          { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' }, null] as const,
      ] as const
    ).map(([metricKey, label, unit, direction, measureIdentity, comparisonAnchorBase]) =>
      metricEntry({
        metricKey,
        label,
        definition: `Governed finance metric (${label.toLowerCase()}) published by the finance module's target-gap and trend diagnostics.`,
        reconciliationRule: 'Recomputed by the finance module target-gap / trend formulas from approved finance evidence for the same reporting cut.',
        source: `apps/api/src/decision-modules/finance/finance.engine.ts#metricKey:${metricKey}`,
        ownerModuleId: 'finance',
        unit,
        ...(unit === 'percent' ? { frame: `${metricKey}`, temporalType: 'ratio' as const, blendPolicy: { kind: 'never_blend' as const } } : {}),
        direction,
        sourceOfTruthClass: 'derived',
        sourceBindingKeys: [metricKey],
        benchmarkable: unit !== 'points',
        measureIdentity,
        ...(comparisonAnchorBase
          ? {
              targetComparisonAnchor: {
                relation: 'target_gap_of' as const,
                metricRef: {
                  metricId: comparisonAnchorBase,
                  registryVersion: METRIC_REGISTRY_SCHEMA_VERSION,
                },
              },
            }
          : {}),
      }),
    ),
    // --- objective-value-bridge scenario — 3 keys, 0/3 previously compiled ----------
    metricEntry({
      metricKey: 'scenario_approved_funding_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Scenario: approved funding',
      definition: 'Approved investment funding backing a governed value-bridge scenario case.',
      reconciliationRule: 'Direct profile-mapped line from the approved scenario funding evidence.',
      source: 'apps/api/src/decision-modules/finance/finance.scenarios.ts#metricKey:scenario_approved_funding_usd',
      ownerModuleId: 'finance',
      registryVersion: 'objective_value_bridge_v1',
      unit: 'USD',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['scenario_approved_funding_usd'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'scenario_investment_requirements_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Scenario: investment requirements',
      definition: 'Required investment for a governed value-bridge scenario case to realize its benefit.',
      reconciliationRule: 'Direct profile-mapped line from the approved scenario investment-requirements evidence.',
      source: 'apps/api/src/decision-modules/finance/finance.scenarios.ts#metricKey:scenario_investment_requirements_usd',
      ownerModuleId: 'finance',
      registryVersion: 'objective_value_bridge_v1',
      unit: 'USD',
      direction: 'lower_is_better',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['scenario_investment_requirements_usd'],
      benchmarkable: false,
    }),
    metricEntry({
      metricKey: 'scenario_value_case_risk_adjusted_benefit_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Scenario: risk-adjusted benefit',
      definition: 'Risk-adjusted benefit of a governed value-bridge scenario case, after approved confidence weighting.',
      reconciliationRule: 'Derived: scenario gross benefit times the approved risk-adjustment factor for the case.',
      source: 'apps/api/src/decision-modules/finance/finance.scenarios.ts#metricKey:scenario_value_case_risk_adjusted_benefit_usd',
      ownerModuleId: 'finance',
      registryVersion: 'objective_value_bridge_v1',
      unit: 'USD',
      direction: 'higher_is_better',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['scenario_value_case_risk_adjusted_benefit_usd'],
      benchmarkable: false,
    }),
  ]);

export const GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS: readonly MetricRegistryEntry[] =
  Object.freeze([
    ...FINANCE_SEMANTIC_LAYER_REGISTRY_SEED,
    ...OBJECTIVE_VALUE_BRIDGE_METRIC_REGISTRY_SEED,
    ...ORG_ANALYSIS_OPTIMIZATION_METRIC_REGISTRY_SEED,
    ...ORG_GRAPH_READ_METRIC_REGISTRY_SEED,
    ...MA_SYNERGY_TRACKING_METRIC_REGISTRY_SEED,
    ...MA_DEAL_METRIC_REGISTRY_SEED,
    ...HELIX_RETAIL_MODELED_PACK_REGISTRY_SEED_20260828,
    ...GL_DATA_LAKE_METRIC_REGISTRY_SEED,
    ...SGA_SPEND_SOURCE_METRIC_REGISTRY_SEED,
    ...INDUSTRY_BENCHMARK_LENS_METRIC_REGISTRY_SEED,
  ]);

export const METRIC_REGISTRY_NAMED_GAPS: readonly MetricRegistryNamedGap[] = Object.freeze([
  Object.freeze({
    gapId: 'company_metric_overlay_not_mapped' as const,
    companyMappingId: 'helix_retail' as const,
    industryProfileId: '3pl-logistics' as const,
    reason: 'no_source_company_mapping_exists' as const,
  }),
]);

export function resolveMetricTargetReference(
  reference: MetricRegistryTargetRef,
): MetricRegistryEntry | undefined {
  const definitionsAtVersion = GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS.filter(
    (entry) => entry.registryVersion === reference.registryVersion,
  );
  if (definitionsAtVersion.length === 0) return undefined;
  return createMetricRegistry(definitionsAtVersion).resolveMetric(reference.metricId);
}

/**
 * Resolve a registered_metric id across EVERY compiled registry version. A
 * module's registered_metric declaration names no version, and the compiled
 * definitions span more than one (metric_registry_v1, objective_value_bridge_v1)
 * — pinning one version would falsely reject metrics that live in the other.
 * Undefined unless EXACTLY ONE version resolves the id: resolving in none means
 * the metric does not exist; resolving in several is ambiguous (the declaration
 * cannot say which identity it claims). Fail closed either way.
 */
export function resolveRegisteredMetricAcrossCompiledVersions(
  metricId: string,
): MetricRegistryEntry | undefined {
  const versions = [
    ...new Set(
      GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS.map(
        (entry) => entry.registryVersion,
      ).filter((version): version is string => typeof version === 'string'),
    ),
  ];
  const resolved = versions
    .map((registryVersion) => resolveMetricTargetReference({ metricId, registryVersion }))
    .filter((entry): entry is MetricRegistryEntry => entry !== undefined);
  return resolved.length === 1 ? resolved[0] : undefined;
}

export type MetricRegistry = ReturnType<typeof createMetricRegistry>;

export function createMetricRegistry(
  entries: readonly MetricRegistryEntry[],
  dimensions: readonly DimensionCatalogEntry[] = GOVERNED_DIMENSION_CATALOG_SEED,
) {
  const byKey = new Map(entries.map((entry) => [entry.metricKey, entry]));
  const byAlias = new Map<string, MetricRegistryEntry>();
  const ambiguousAliases = new Set<string>();
  for (const entry of entries) {
    for (const alias of entry.aliases ?? []) {
      if (byKey.has(alias) || byAlias.has(alias)) {
        byAlias.delete(alias);
        ambiguousAliases.add(alias);
        continue;
      }
      if (!ambiguousAliases.has(alias)) byAlias.set(alias, entry);
    }
  }
  const dimensionsByKey = new Map(dimensions.map((dimension) => [dimension.dimension, dimension]));

  const validateQuerySpec = (
    spec: MetricQuerySpec,
    dimensionMemberCounts: Readonly<Record<string, number>> = {},
  ): MetricRegistryValidationResult => {
    const missingMetricKey = spec.metrics.find((metricKey) => !byKey.has(metricKey));
    if (missingMetricKey) {
      return refusal('metric_not_found', `Metric ${missingMetricKey} is not registered.`, {
        metricKey: missingMetricKey,
      });
    }

    const rollupResult = validateDeclaredRollupGrains(byKey, spec);
    if (!rollupResult.ok) {
      return rollupResult;
    }

    const requestedDimension = spec.grain.level[0]?.dimension ?? spec.groupBy[0] ?? 'metric';
    const requestedMemberKeys = spec.filters
      .filter((filter) => filter.dimension === requestedDimension)
      .flatMap((filter) => [...filter.memberKeys]);
    const blendResult = validateMetricBlend(byKey, dimensionsByKey, {
      metricKeys: spec.metrics,
      dimension: requestedDimension,
      memberKeys: requestedMemberKeys,
    });
    if (!blendResult.ok) {
      return blendResult;
    }

    return validateMetricGrainBudget(spec, dimensionMemberCounts);
  };

  return {
    getMetric(metricKey: string): MetricRegistryEntry | undefined {
      return byKey.get(metricKey);
    },

    resolveMetric(metricKey: string): MetricRegistryEntry | undefined {
      return byKey.get(metricKey) ?? byAlias.get(metricKey);
    },

    listMetrics(): readonly MetricRegistryEntry[] {
      return entries;
    },

    listMetricEntries(): readonly MetricRegistryEntry[] {
      return entries;
    },

    getDimensionCatalog(dimension: string): DimensionCatalogEntry | undefined {
      return dimensionsByKey.get(dimension);
    },

    getRollupGrains(metricKey: string): readonly MetricRollupGrain[] {
      const entry = byKey.get(metricKey);
      if (!entry) return [];
      return entry.aggregationRules.map((rule) => ({
        dimension: rule.dimension,
        rollup: rule.rollup,
      }));
    },

    getRollupGrainDeclarations(metricKey: string): readonly MetricRegistryAggregationRule[] {
      return byKey.get(metricKey)?.aggregationRules ?? [];
    },

    validateBlend(input: MetricBlendRequest): MetricRegistryValidationResult {
      return validateMetricBlend(byKey, dimensionsByKey, input);
    },

    validateGrainBudget(
      spec: MetricQuerySpec,
      dimensionMemberCounts: Readonly<Record<string, number>>,
    ): MetricRegistryValidationResult {
      return validateMetricGrainBudget(spec, dimensionMemberCounts);
    },

    validateQuerySpec,
    disposeQuerySpec: validateQuerySpec,
  };
}
function validateMetricBlend(
  byKey: ReadonlyMap<string, MetricRegistryEntry>,
  dimensionsByKey: ReadonlyMap<string, DimensionCatalogEntry>,
  input: MetricBlendRequest,
): MetricRegistryValidationResult {
  const selected = input.metricKeys.map((metricKey) => byKey.get(metricKey));
  const missingMetricKey = input.metricKeys.find((_metricKey, index) => !selected[index]);
  if (missingMetricKey) {
    return refusal('metric_not_found', `Metric ${missingMetricKey} is not registered.`, {
      metricKey: missingMetricKey,
    });
  }

  const entriesForBlend = selected as MetricRegistryEntry[];
  const invalidEntry = entriesForBlend.find(
    (entry) => !entryAppliesToMembers(entry, input.dimension, input.memberKeys),
  );
  if (invalidEntry) {
    return refusal(
      'dimension_member_invalid',
      'Requested dimension member is outside the metric registry appliesTo scope.',
      {
        dimension: input.dimension,
        memberKeys: input.memberKeys,
        metricKey: invalidEntry.metricKey,
        appliesTo: invalidEntry.appliesTo,
      },
    );
  }

  const frames = new Set(entriesForBlend.map((entry) => entry.frame));
  const hasNeverBlendMetric = entriesForBlend.some(
    (entry) => entry.blendPolicy.kind === 'never_blend',
  );
  const dimensionCatalog = dimensionsByKey.get(input.dimension);
  const dimensionNeverBlendApplies =
    dimensionCatalog?.neverBlend?.appliesTo.some((scope) =>
      scopeAppliesToMembers(scope, input.dimension, input.memberKeys),
    ) ?? false;

  if (frames.size > 1 && (hasNeverBlendMetric || dimensionNeverBlendApplies)) {
    return refusal(
      'blend_forbidden',
      'Requested metrics cross registry frames that are declared never-blend.',
      {
        dimension: input.dimension,
        memberKeys: input.memberKeys,
        frames: Array.from(frames).sort(),
        metricKeys: input.metricKeys,
      },
    );
  }

  return { ok: true };
}

function validateDeclaredRollupGrains(
  byKey: ReadonlyMap<string, MetricRegistryEntry>,
  spec: MetricQuerySpec,
): MetricRegistryValidationResult {
  for (const metricKey of spec.metrics) {
    const entry = byKey.get(metricKey);
    if (!entry) continue;

    for (const requestedGrain of spec.grain.level) {
      const hasDeclaration = entry.aggregationRules.some(
        (rule) =>
          rule.dimension === requestedGrain.dimension && rule.rollup === requestedGrain.rollup,
      );
      if (!hasDeclaration) {
        return refusal(
          'rollup_grain_not_declared',
          'Requested metric grain is not declared by the governed registry.',
          { metricKey, requestedGrain },
        );
      }
    }
  }

  return { ok: true };
}

function validateMetricGrainBudget(
  spec: MetricQuerySpec,
  dimensionMemberCounts: Readonly<Record<string, number>>,
): MetricRegistryValidationResult {
  const requestedDimensions = spec.grain.level.map((level) => level.dimension);
  const cellMultiplier = requestedDimensions.reduce((product, dimension) => {
    return product * (dimensionMemberCounts[dimension] ?? 1);
  }, 1);
  const estimatedCells = spec.metrics.length * Math.max(1, cellMultiplier);
  const maxCells = spec.budget?.maxCells;

  if (maxCells !== undefined && estimatedCells > maxCells) {
    return refusal(
      'grain_budget_exceeded',
      'Requested metric grain exceeds the declared result budget.',
      { estimatedCells, maxCells },
      { grain: { level: [{ dimension: 'business_line', rollup: 'enterprise' }] } },
    );
  }

  return { ok: true };
}

function entryAppliesToMembers(
  entry: MetricRegistryEntry,
  dimension: string,
  memberKeys: readonly string[],
): boolean {
  const scopes = entry.appliesTo.filter((scope) => scope.dimension === dimension);
  if (scopes.length === 0) return true;
  return scopes.some((scope) => scopeAppliesToMembers(scope, dimension, memberKeys));
}

function scopeAppliesToMembers(
  scope: MetricRegistryAppliesTo,
  dimension: string,
  memberKeys: readonly string[],
): boolean {
  if (scope.dimension !== dimension) return false;
  if (scope.members === 'all') return true;
  if (memberKeys.length === 0) return true;
  return memberKeys.some((memberKey) => scope.members.includes(memberKey));
}
export function buildGovernedMetricFactDedupKey(input: GovernedMetricFactDedupInput): string {
  return [
    input.tenantId,
    input.metricKey,
    dimensionKey(input.businessLineKey),
    dimensionKey(input.geoKey),
    dimensionKey(input.customerKey),
    dimensionKey(input.laneKey),
    dimensionKey(input.siteKey),
    input.periodType,
    input.periodKey,
    input.asOf,
  ].join('|');
}

export function createGovernedMetricFactDeduper() {
  const keys = new Set<string>();

  return {
    upsert(input: GovernedMetricFactDedupInput): GovernedMetricFactDedupResult {
      const key = buildGovernedMetricFactDedupKey(input);
      const inserted = !keys.has(key);
      keys.add(key);
      return { inserted, key };
    },

    get size(): number {
      return keys.size;
    },
  };
}

function dimensionKey(value: string | null | undefined): string {
  return value ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY;
}

type MetricEntryInput = Readonly<{
  metricKey: string;
  label: string;
  definition: string;
  reconciliationRule: string;
  source: string;
  registryLayer?: MetricRegistryLayer;
  industryProfileId?: string;
  registryVersion?: string;
  appliesTo?: readonly MetricRegistryAppliesTo[];
  unit?: string;
  frame?: string;
  direction?: MetricDirection;
  blendPolicy?: MetricRegistryBlendPolicy;
  aggregationRules?: readonly MetricRegistryAggregationRule[];
  grainSupported?: readonly string[];
  dimensionsSupported?: readonly string[];
  benchmarkable?: boolean;
  components?: MetricRegistryComponents;
  temporalType?: MetricTemporalType;
  measureIdentity?: GovernedMeasureIdentity;
  cohortQualifier?: MetricRegistryCohortQualifier;
  aliases?: readonly string[];
  signConvention?: MetricRegistrySignConvention;
  sourceOfTruthClass?: MetricRegistrySourceOfTruthClass;
  factScope?: MetricRegistryEntry['factScope'];
  composedFrom?: MetricRegistryComposition;
  targetMetricRef?: MetricRegistryTargetRef;
  targetComparisonAnchor?: MetricTargetComparisonAnchor;
  ownerModuleId?: string;
  neverSumWith?: readonly string[];
  sourceBindingKeys?: readonly string[];
  requiredEvidenceClasses?: readonly string[];
}>;

function metricEntry(input: MetricEntryInput): MetricRegistryEntry {
  const isContributionOutput = input.requiredEvidenceClasses?.includes(
    'module_composite_contribution_v1',
  );
  return {
    metricKey: input.metricKey,
    registryVersion: input.registryVersion ?? METRIC_REGISTRY_SCHEMA_VERSION,
    registryLayer: input.registryLayer ?? 'core',
    industryProfileId: input.industryProfileId,
    appliesTo:
      input.appliesTo ??
      (isContributionOutput
        ? []
        : [{ dimension: 'business_line', members: 'all' }]),
    label: input.label,
    definition: input.definition,
    unit: input.unit ?? 'USD',
    frame: input.frame ?? 'level',
    direction: input.direction ?? 'higher_is_better',
    blendPolicy:
      input.blendPolicy ?? (isContributionOutput ? { kind: 'never_blend' } : { kind: 'additive' }),
    aggregationRules:
      input.aggregationRules ?? (isContributionOutput ? [] : USD_AGGREGATION_RULES),
    reconciliationRule: input.reconciliationRule,
    requiredEvidenceClasses: input.requiredEvidenceClasses ?? ['finance_pnl_governed_snapshot_v1'],
    benchmarkable: input.benchmarkable ?? true,
    ...(input.grainSupported ? { grainSupported: input.grainSupported } : {}),
    ...(input.dimensionsSupported ? { dimensionsSupported: input.dimensionsSupported } : {}),
    components: input.components,
    temporalType: input.temporalType ?? 'flow',
    measureIdentity: input.measureIdentity,
    cohortQualifier: input.cohortQualifier,
    aliases: input.aliases,
    signConvention: input.signConvention,
    sourceOfTruthClass: input.sourceOfTruthClass ?? 'public-filing',
    ...(input.factScope ? { factScope: input.factScope } : {}),
    composedFrom: input.composedFrom,
    targetMetricRef: input.targetMetricRef,
    targetComparisonAnchor: input.targetComparisonAnchor,
    ownerModuleId: input.ownerModuleId,
    neverSumWith: input.neverSumWith,
    // Source bindings are evidence; the property checker fails omissions.
    sourceBindingKeys: input.sourceBindingKeys,
    source: input.source,
  };
}
// #5544 shrink-on-touch: `metricEntry` stays module-private, so leaf files that
// export raw seed-input data for it to map over (metric-registry.sga-spend-existing.data.ts,
// same pattern as metric-registry.ma-synergy-tracking.data.ts) need its exact
// input type without importing the function itself.
export type MetricEntrySeedInput = Parameters<typeof metricEntry>[0];
function ratioMetricEntry(
  input: Readonly<{
    metricKey: string;
    label: string;
    definition: string;
    frame: string;
    source: string;
    numerator: readonly string[];
    denominator: readonly string[];
    appliesTo: readonly MetricRegistryAppliesTo[];
    sourceBindingKeys: readonly string[];
    // Structural fix (#5734 measure-identity sweep): this builder previously had
    // no way to accept measureIdentity at all, so every row it produced (the two
    // industry ratio rows below) was unconditionally at risk of the admission
    // fence's `unverified` verdict. Optional, same as metricEntry()'s field.
    measureIdentity?: GovernedMeasureIdentity;
  }>,
): MetricRegistryEntry {
  return {
    metricKey: input.metricKey,
    registryVersion: METRIC_REGISTRY_SCHEMA_VERSION,
    registryLayer: 'industry',
    industryProfileId: '3pl-logistics',
    appliesTo: input.appliesTo,
    label: input.label,
    definition: input.definition,
    unit: 'percent',
    frame: input.frame,
    direction: 'higher_is_better',
    blendPolicy: { kind: 'never_blend' },
    aggregationRules: RATIO_AGGREGATION_RULES,
    reconciliationRule: 'Ratio is recomputed from registered numerator and denominator facts.',
    requiredEvidenceClasses: ['finance_pnl_governed_snapshot_v1'],
    benchmarkable: true,
    components: components(input.numerator, input.denominator),
    temporalType: 'ratio',
    measureIdentity: input.measureIdentity,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'finance',
    sourceBindingKeys: input.sourceBindingKeys,
    source: input.source,
  };
}

function components(
  numerator: readonly string[],
  denominator: readonly string[],
  denominatorTemporalType: Extract<MetricTemporalType, 'flow' | 'stock' | 'count'> = 'flow',
): MetricRegistryComponents {
  return {
    numerator: componentRefs(numerator, 'flow'),
    denominator: componentRefs(denominator, denominatorTemporalType),
  };
}

function componentRefs(
  metricKeys: readonly string[],
  temporalType: Extract<MetricTemporalType, 'flow' | 'stock' | 'count'>,
): readonly MetricComponentRef[] {
  return metricKeys.map((metricKey) => ({ metricKey, temporalType }));
}
function refusal(
  reason: MetricRegistryRefusalReason,
  message: string,
  details?: Readonly<Record<string, unknown>>,
  counterProposal?: Partial<MetricQuerySpec>,
): MetricRegistryRefused {
  return { ok: false, reason, message, details, counterProposal };
}
